import './style.css';
import { RATINGS, RESULT_LIMIT, type Rating } from './config';
import { GiphyError, searchGifs, trendingGifs } from './api/giphy';
import { store } from './state';
import type { GiphyGif } from './types';
import { renderAnimalButtons, setActiveAnimal } from './ui/animalButtons';
import {
  bindCardActions,
  renderGifs,
  renderSkeletons,
  renderStatus,
} from './ui/gifGrid';

// --- Element handles --------------------------------------------------------
const $ = <T extends HTMLElement>(sel: string) => document.querySelector<T>(sel)!;

const searchForm = $<HTMLFormElement>('#search-form');
const searchInput = $<HTMLInputElement>('#search-input');
const ratingSelect = $<HTMLSelectElement>('#rating-select');
const addButton = $<HTMLButtonElement>('#add-button');
const themeToggle = $<HTMLButtonElement>('#theme-toggle');
const loadMoreBtn = $<HTMLButtonElement>('#load-more');
const resultsTitle = $<HTMLHeadingElement>('#results-title');
const favCount = $<HTMLSpanElement>('#fav-count');
const tabs = document.querySelectorAll<HTMLButtonElement>('.tab');
const quickSection = $<HTMLElement>('#quick-buttons-section');
const historyWrap = $<HTMLDivElement>('#history');
const historyChips = $<HTMLDivElement>('#history-chips');

// --- Current browse context (for "load more" + card lookup) -----------------
type Source = { kind: 'trending' } | { kind: 'search'; query: string };
let currentSource: Source = { kind: 'trending' };
let offset = 0;
let totalCount = 0;
let resultGifs: GiphyGif[] = [];

/** Resolve a card id to its GiphyGif from the active view. */
function lookup(id: string): GiphyGif | undefined {
  const pool =
    store.get().view === 'favorites' ? store.get().favorites : resultGifs;
  return pool.find((g) => g.id === id);
}

// --- Fetching ---------------------------------------------------------------
async function load(append: boolean): Promise<void> {
  const rating = store.get().rating;
  if (!append) {
    offset = 0;
    renderSkeletons();
    loadMoreBtn.hidden = true;
  } else {
    loadMoreBtn.disabled = true;
    loadMoreBtn.textContent = 'Loading…';
  }

  try {
    const page =
      currentSource.kind === 'search'
        ? await searchGifs(currentSource.query, { rating, offset })
        : await trendingGifs({ rating, offset });

    totalCount = page.totalCount;

    if (!append) {
      resultGifs = page.gifs;
      if (resultGifs.length === 0) {
        renderStatus('empty');
        return;
      }
      renderGifs(resultGifs, false);
    } else {
      resultGifs = resultGifs.concat(page.gifs);
      renderGifs(page.gifs, true);
    }

    offset += page.gifs.length;
    updateLoadMore();
  } catch (err) {
    const msg = err instanceof GiphyError ? err.message : undefined;
    if (!append) renderStatus('error', msg);
  } finally {
    loadMoreBtn.disabled = false;
    loadMoreBtn.textContent = 'Load more';
  }
}

function updateLoadMore(): void {
  const more = resultGifs.length < totalCount && resultGifs.length >= RESULT_LIMIT;
  loadMoreBtn.hidden = store.get().view === 'favorites' || !more;
}

// --- Search actions ---------------------------------------------------------
function runSearch(term: string): void {
  const query = term.trim();
  if (!query) return;
  store.recordSearch(query);
  store.setView('browse');
  currentSource = { kind: 'search', query };
  resultsTitle.textContent = `Results for “${query}”`;
  setActiveAnimal(store.get().animals.includes(query) ? query : null);
  void load(false);
}

function showTrending(): void {
  currentSource = { kind: 'trending' };
  resultsTitle.textContent = 'Trending';
  setActiveAnimal(null);
  void load(false);
}

// --- Favorites view ---------------------------------------------------------
function showFavorites(): void {
  const favs = store.get().favorites;
  resultsTitle.textContent = `Favorites (${favs.length})`;
  loadMoreBtn.hidden = true;
  if (favs.length === 0) {
    renderStatus('empty', 'Tap the ☆ on any GIF to save it here.');
  } else {
    renderGifs(favs, false);
  }
}

// --- Reactive rendering ------------------------------------------------------
store.subscribe((s) => {
  favCount.textContent = String(s.favorites.length);
  renderHistory();
  quickSection.hidden = s.view !== 'browse';
  // Keep the favorites grid in sync (e.g. removing a card by unfavoriting it).
  if (s.view === 'favorites') showFavorites();
});

function renderHistory(): void {
  const { history } = store.get();
  historyWrap.hidden = history.length === 0;
  historyChips.innerHTML = history
    .map((h) => `<button class="chip" type="button" data-term="${h}">${h}</button>`)
    .join('');
}

function renderQuickButtons(): void {
  renderAnimalButtons((animal) => {
    searchInput.value = animal;
    runSearch(animal);
    setActiveAnimal(animal);
  });
}
store.subscribe(renderQuickButtons);

// --- Theme -------------------------------------------------------------------
function applyTheme(): void {
  const theme = store.get().theme;
  document.documentElement.setAttribute('data-theme', theme);
  themeToggle.querySelector('.theme-icon')!.textContent = theme === 'dark' ? '🌙' : '☀️';
}

// --- Event wiring ------------------------------------------------------------
function bindEvents(): void {
  searchForm.addEventListener('submit', (e) => {
    e.preventDefault();
    if (searchInput.value.trim()) runSearch(searchInput.value);
    else showTrending();
  });

  addButton.addEventListener('click', () => {
    const term = searchInput.value.trim();
    if (!term) {
      searchInput.focus();
      return;
    }
    if (store.addAnimal(term)) {
      runSearch(term);
      setActiveAnimal(term);
    }
  });

  ratingSelect.addEventListener('change', () => {
    store.setRating(ratingSelect.value as Rating);
    if (store.get().view === 'browse') void load(false);
  });

  loadMoreBtn.addEventListener('click', () => void load(true));

  themeToggle.addEventListener('click', () => {
    store.toggleTheme();
    applyTheme();
  });

  historyChips.addEventListener('click', (e) => {
    const term = (e.target as HTMLElement).closest<HTMLElement>('[data-term]')?.dataset.term;
    if (!term) return;
    searchInput.value = term;
    runSearch(term);
  });

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const view = tab.dataset.view as 'browse' | 'favorites';
      tabs.forEach((t) => t.classList.toggle('is-active', t === tab));
      store.setView(view); // subscriber renders the favorites grid
      if (view === 'browse') {
        if (currentSource.kind === 'search') runSearch(currentSource.query);
        else showTrending();
      }
    });
  });

  bindCardActions(lookup);
}

// --- Init -------------------------------------------------------------------
function init(): void {
  // Seed the rating dropdown from persisted preference.
  const saved = store.get().rating;
  if (RATINGS.includes(saved)) ratingSelect.value = saved;

  applyTheme();
  renderQuickButtons();
  renderHistory();
  favCount.textContent = String(store.get().favorites.length);
  bindEvents();
  showTrending();
}

init();
